import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase.utils';
import { addCardsToFirestore } from '../firebase/collection';
import { logUserAction } from '../firebase/activity';
import { getGroupCode, normalizeGroupCode } from '../firebase/groupCodes';
import { addToQueueStructure, resolveCardPath } from '../firebase/releaseCards';
import { isListed } from '../sales/sales.constants';
import { sanitizePreviewCard } from './groupCodePreview';
import { invalidateCollection } from '../../store/card/card.action';
import { setSystemNotification } from '../../store/system/system.action';

const pendingDocRef = (uid, code) => (
  doc(db, `users/${uid}/pendingGroupCards`, normalizeGroupCode(code))
);

const seriesUnlockKey = (cardPath) => (
  `${cardPath.sport}_${cardPath.year}_${cardPath.manufacturer}_${cardPath.series}`
);

const pendingCardKey = (card) => {
  const variation = card?.variation ?? {};
  return [
    card?.cardNumber,
    variation.cardParallelName,
    variation.cardPrintNumber,
    variation.cardSupply,
    card?.cardPath?.series
  ].join('|');
};

const getUnlockedSeriesKeys = async (uid) => {
  const snap = await getDoc(doc(db, `users/${uid}/unlockedChecklists`, 'allUnlocked'));
  if (!snap.exists()) return new Set();

  const keys = new Set();
  Object.entries(snap.data() ?? {}).forEach(([key, value]) => {
    if (value?.isUnlocked) keys.add(key);
  });
  return keys;
};

const mergePendingCards = (existing, incoming) => {
  const merged = [...existing];
  const seen = new Set(existing.map(pendingCardKey));
  incoming.forEach((card) => {
    const key = pendingCardKey(card);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(sanitizePreviewCard(card));
  });
  return merged;
};

const writePendingCards = async (uid, code, title, cards) => {
  if (!uid || !code || cards.length === 0) return;
  const ref = pendingDocRef(uid, code);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? (snap.data().cards ?? []) : [];

  await setDoc(ref, {
    code: normalizeGroupCode(code),
    title: title ?? null,
    cards: mergePendingCards(existing, cards),
    updatedAt: serverTimestamp()
  }, { merge: true });
};

const getCollectionCount = async (uid) => {
  const snap = await getDoc(doc(db, `users/${uid}`));
  return (snap.exists() ? snap.data().collectionCardCount : 0) || 0;
};

const addPreviewCardsToCollection = async (uid, cards, { groupCode, title, skippedCount, prevCount }) => {
  const queuedCards = { totalCardCount: 0 };
  cards.forEach((card) => {
    const cardPath = resolveCardPath(card);
    if (cardPath) addToQueueStructure(queuedCards, cardPath, card);
  });

  if (queuedCards.totalCardCount > 0) {
    await addCardsToFirestore(uid, queuedCards);
  }

  logUserAction(uid, 'addedToCollection', {
    cards: queuedCards,
    prevCount,
    increment: queuedCards.totalCardCount,
    skippedCount,
    groupCode,
    title
  });

  return queuedCards.totalCardCount;
};

const notifyImportResult = (dispatch, addedCount, skippedCount) => {
  if (addedCount > 0 && skippedCount > 0) {
    dispatch(setSystemNotification(
      `Added ${addedCount} ${addedCount === 1 ? 'card' : 'cards'}. ${skippedCount} need a checklist unlock.`
    ));
    return;
  }
  if (addedCount > 0) {
    dispatch(setSystemNotification(
      `Added ${addedCount} ${addedCount === 1 ? 'card' : 'cards'} to your collection.`
    ));
    return;
  }
  if (skippedCount > 0) {
    dispatch(setSystemNotification(
      `${skippedCount} ${skippedCount === 1 ? 'card needs' : 'cards need'} a checklist unlock before they can be added.`
    ));
    return;
  }
  dispatch(setSystemNotification('No cards could be added for that code.'));
};

/**
 * Copies cards from a group code into the signed-in user's collection.
 * Unlocked checklists are added now; the rest are stored as pending.
 */
export const importGroupCode = (uid, rawCode) => {
  return async (dispatch) => {
    const code = normalizeGroupCode(rawCode);
    if (!uid || !code) return { addedCount: 0, skippedCount: 0 };

    const group = await getGroupCode(code);
    const cards = group?.cards ?? [];
    if (!group || cards.length === 0) {
      dispatch(setSystemNotification('No cards found for that code.'));
      return { addedCount: 0, skippedCount: 0 };
    }

    if (group.status && isListed(group.status)) {
      dispatch(setSystemNotification('This listing is not complete yet.'));
      return { addedCount: 0, skippedCount: 0 };
    }

    const unlocked = await getUnlockedSeriesKeys(uid);
    const addable = [];
    const pending = [];

    cards.forEach((card) => {
      const cardPath = resolveCardPath(card);
      if (!cardPath || !unlocked.has(seriesUnlockKey(cardPath))) {
        pending.push(card);
        return;
      }
      addable.push(card);
    });

    const prevCount = await getCollectionCount(uid);
    const addedCount = await addPreviewCardsToCollection(uid, addable, {
      groupCode: code,
      title: group.title,
      skippedCount: pending.length,
      prevCount
    });

    await writePendingCards(uid, code, group.title, pending);
    dispatch(invalidateCollection());
    notifyImportResult(dispatch, addedCount, pending.length);

    return { addedCount, skippedCount: pending.length };
  };
};

/**
 * After a checklist unlock, offer any pending cards that belong to that series.
 */
export const addPendingGroupCardsForSeries = (uid, sport, year, manufacturer, series) => {
  return async (dispatch) => {
    if (!uid || !sport || !year || !manufacturer || !series) return 0;

    const snap = await getDocs(collection(db, `users/${uid}/pendingGroupCards`));
    if (snap.empty) return 0;

    let addedCount = 0;
    const prevCount = await getCollectionCount(uid);
    let runningPrev = prevCount;

    for (const pendingDoc of snap.docs) {
      const data = pendingDoc.data() ?? {};
      const cards = data.cards ?? [];
      const matching = [];
      const remaining = [];

      cards.forEach((card) => {
        const cardPath = resolveCardPath(card);
        if (
          cardPath
          && cardPath.sport === sport
          && cardPath.year === year
          && cardPath.manufacturer === manufacturer
          && cardPath.series === series
        ) {
          matching.push(card);
        } else {
          remaining.push(card);
        }
      });

      if (matching.length === 0) continue;

      const added = await addPreviewCardsToCollection(uid, matching, {
        groupCode: data.code ?? pendingDoc.id,
        title: data.title,
        skippedCount: remaining.length,
        prevCount: runningPrev
      });
      addedCount += added;
      runningPrev += added;

      if (remaining.length === 0) {
        await deleteDoc(pendingDoc.ref);
      } else {
        await setDoc(pendingDoc.ref, {
          ...data,
          cards: remaining,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    }

    if (addedCount > 0) {
      dispatch(invalidateCollection());
      dispatch(setSystemNotification(
        `Added ${addedCount} ${addedCount === 1 ? 'card' : 'cards'} from a previous code.`
      ));
    }

    return addedCount;
  };
};

export const countPendingGroupCardsForSeries = async (uid, sport, year, manufacturer, series) => {
  if (!uid) return 0;
  const snap = await getDocs(collection(db, `users/${uid}/pendingGroupCards`));
  let count = 0;
  snap.docs.forEach((pendingDoc) => {
    (pendingDoc.data()?.cards ?? []).forEach((card) => {
      const cardPath = resolveCardPath(card);
      if (
        cardPath
        && cardPath.sport === sport
        && cardPath.year === year
        && cardPath.manufacturer === manufacturer
        && cardPath.series === series
      ) {
        count += 1;
      }
    });
  });
  return count;
};
